import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  Phone,
  AlertCircle,
  Truck,
  Clock,
  MessageSquare,
  MapPin,
  Navigation,
  Home,
  Circle
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { supabase, fetchPublicReceipt } from '../supabaseClient';
import { ITEM_TERMINAL_STATUSES, ITEM_STAGED_STATUSES } from '../utils/statusConstants';

// delivery_date is a plain "YYYY-MM-DD" from the dashboard's date input.
// `new Date("YYYY-MM-DD")` parses that as UTC midnight, which in US
// timezones displays as the evening BEFORE, so the customer saw their
// delivery a day early. Build plain dates as local calendar days instead.
const parseDateOnly = (value) => {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// ============================================================
// BRAND TOKENS — every brand-owned color on the receipt, in one place.
//
// Status colors are NOT here on purpose. Green/blue/amber mean
// "arrived / on the truck / still coming" to a customer, and a
// customer reads them faster than they read any label. Repainting
// them in brand colors would cost more comprehension than it buys
// recognition, so brand lives in the surfaces and the marks, and
// meaning lives in the status hues.
//
// Swap these five values and the whole sheet retunes.
// ============================================================
const BRAND = {
  // Page behind the sheet. Kept light — the sheet should read as
  // paper on a desk, not a card floating in a dark room.
  canvas: '#F4F6F9',
  // The 3px letterhead rule across the top edge of the sheet.
  accent: '#0F62A8',
  accentSoft: '#3B8FD4',
  // Ink. Slightly blue-cast neutral so it sits with the accent
  // instead of fighting it.
  ink: '#131A24',
  hairline: 'rgba(19, 26, 36, 0.08)'
};

const DigitalReceipt = () => {
  const { receiptId } = useParams();
  
  const [receipt, setReceipt] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(true);
  const [error, setError] = useState(null);
  
  // Delivery tracking state
  const [deliveryRoute, setDeliveryRoute] = useState(null);
  const [routeStops, setRouteStops] = useState([]);
  const [currentStopIndex, setCurrentStopIndex] = useState(-1);
  const [myStopIndex, setMyStopIndex] = useState(-1);
  
  // Item details drawer (keeps SKU / item number out of the default customer view)
  const [expandedItems, setExpandedItems] = useState({});
  const [showAllItems, setShowAllItems] = useState(false);
  const toggleItemDetails = (index) => {
    setExpandedItems(prev => ({ ...prev, [index]: !prev[index] }));
  };

  // Load delivery route when receipt is out for delivery
  const loadDeliveryRoute = async () => {
    if (!receiptId) {
      console.log('❌ loadDeliveryRoute: No receiptId available');
      return;
    }
    
    console.log('🔍 Loading delivery route for receipt:', receiptId);
    console.log('🔍 Current state - deliveryRoute:', !!deliveryRoute, 'routeStops:', routeStops.length);
    
    try {
      // Find the route stop for this receipt - don't filter by stop status initially
      const { data: stopData, error: stopError } = await supabase
        .from('delivery_route_stops')
        .select(`
          *,
          delivery_routes (
            id,
            route_name,
            status,
            driver_name,
            driver_phone,
            driver_latitude,
            driver_longitude,
            started_at,
            dispatched_at,
            estimated_completion
          )
        `)
        .eq('receipt_id', receiptId)
        .order('created_at', { ascending: false })
        .limit(1);
      
      console.log('🔍 Route stop query result:', { stopData, stopError });
      
      if (stopError) {
        console.error('❌ Error querying route stop:', stopError);
        setDeliveryRoute(null);
        setRouteStops([]);
        return;
      }
      
      if (!stopData || stopData.length === 0) {
        console.log('❌ No route stop found for this receipt - receipt may not be on a route yet');
        setDeliveryRoute(null);
        setRouteStops([]);
        return;
      }
      
      const myStop = stopData[0];
      const route = myStop.delivery_routes;
      
      console.log('🔍 Found stop:', myStop);
      console.log('🔍 Route data:', route);
      console.log('🔍 Route status:', route?.status);
      
      if (!route) {
        console.log('❌ No route attached to stop - delivery_routes join may have failed');
        setDeliveryRoute(null);
        setRouteStops([]);
        return;
      }
      
      // RELAXED CHECK: Allow 'planned' status too for debugging
      const validStatuses = ['dispatched', 'in_progress', 'planned'];
      if (!validStatuses.includes(route.status)) {
        console.log('⏸️ Route status not active:', route.status, '(expected: dispatched or in_progress)');
        // Still set the route so we can debug, but mark it as not ready
        console.log('⚠️ Route exists but status is:', route.status);
        // Don't return early - let's see what data we have
      }
      
      // Get all stops for this route
      const { data: allStops, error: stopsError } = await supabase
        .from('delivery_route_stops')
        .select('*')
        .eq('route_id', route.id)
        .order('stop_number', { ascending: true });
      
      if (stopsError) {
        console.error('❌ Error loading route stops:', stopsError);
        return;
      }
      
      console.log('🔍 All stops for route:', allStops?.length, 'stops');
      console.log('🔍 Stops detail:', allStops?.map(s => ({ 
        id: s.id, 
        receipt_id: s.receipt_id, 
        stop_number: s.stop_number,
        stop_order: s.stop_order,
        status: s.status 
      })));
      
      // Sort stops properly using stop_order or stop_number
      const sortedStops = (allStops || []).sort((a, b) => {
        return (a.stop_order || a.stop_number || 0) - (b.stop_order || b.stop_number || 0);
      });
      
      // Find current stop (first non-delivered stop)
      const currentIdx = sortedStops.findIndex(s => s.status !== 'delivered');
      
      // Find my stop
      const myIdx = sortedStops.findIndex(s => s.receipt_id === receiptId);
      
      console.log('🚚 Delivery route loaded:', {
        routeId: route.id,
        routeStatus: route.status,
        totalStops: sortedStops.length,
        currentStopIndex: currentIdx,
        myStopIndex: myIdx,
        driverName: route.driver_name
      });
      
      // Only set route data if status is valid
      if (['dispatched', 'in_progress'].includes(route.status)) {
        setDeliveryRoute(route);
        setRouteStops(sortedStops);
        setCurrentStopIndex(currentIdx);
        setMyStopIndex(myIdx);
      } else {
        console.log('⚠️ Not showing tracker - route status is:', route.status);
        console.log('⚠️ Route needs to be dispatched first!');
        setDeliveryRoute(null);
        setRouteStops([]);
      }
      
    } catch (err) {
      console.error('❌ Error loading delivery route:', err);
    }
  };

  // LOAD RECEIPT FROM SUPABASE
  //
  // This page has no login. It used to keep a realtime subscription on
  // receipts / item_statuses, but realtime honours RLS per subscriber, and
  // once the anon SELECT policies are removed (sql/anon-lockdown/02) those
  // events would simply never arrive. A 30 s background refresh through
  // the RPC gives the same "status flips while the customer is looking"
  // behaviour without any table-level anon access. The delivery-route
  // listeners that lived here are gone too: that system is retired and
  // loadDeliveryRoute already polls on its own below.
  useEffect(() => {
    if (!receiptId) return;

    loadReceipt();

    const refresh = setInterval(() => {
      if (document.visibilityState === 'visible') loadReceipt(true);
    }, 30000);

    return () => clearInterval(refresh);
  }, [receiptId]);

  // Load delivery route when receipt changes to out_for_delivery
  // ALSO try loading route even if delivery_status isn't set - in case receipt wasn't updated properly
  useEffect(() => {
    const isOutForDelivery = ['out_for_delivery', 'in_transit', 'partial_out_for_delivery'].includes(receipt?.delivery_status);
    
    console.log('📦 Receipt delivery_status:', receipt?.delivery_status, '| isOutForDelivery:', isOutForDelivery);
    
    // Always try to load delivery route if we have a receipt
    // This handles the case where receipt.delivery_status wasn't updated but route was dispatched
    if (receipt?.id) {
      console.log('🔍 Attempting to load delivery route for receipt:', receipt.id);
      loadDeliveryRoute();
      
      // Poll for updates every 30 seconds for live tracking
      const pollInterval = setInterval(() => {
        loadDeliveryRoute();
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [receipt?.delivery_status, receipt?.id, receiptId]);

  // silent=true is the background refresh: no spinner, and a transient
  // failure keeps the receipt already on screen instead of replacing it
  // with an error.
  const loadReceipt = async (silent = false) => {
    try {
      if (!silent) {
        setLoadingReceipt(true);
        setError(null);
      }

      const data = await fetchPublicReceipt(receiptId);

      if (!data) {
        if (!silent) setError('Receipt not found');
        return;
      }

      // fetchPublicReceipt already returns itemStatuses — no second round trip.
      setReceipt(data);
      console.log('✅ Receipt loaded:', data);
      console.log('📋 Item statuses for receipt:', data.itemStatuses);
      
    } catch (error) {
      console.error('Error loading receipt:', error);
      if (!silent) setError('Failed to load receipt. Please try again.');
    } finally {
      if (!silent) setLoadingReceipt(false);
    }
  };

  // Payment status helper
  const getPaymentStatus = () => {
    if (!receipt) return { status: 'unknown', label: 'Unknown', balance: 0, paid: 0, total: 0 };
    
    const total = parseFloat(receipt.total) || 0;
    const amountPaid = parseFloat(receipt.amount_paid) || 0;
    const balance = total - amountPaid;
    
    if (amountPaid >= total && total > 0) {
      return { status: 'paid', label: 'Paid in Full', balance: 0, paid: amountPaid, total };
    } else if (amountPaid > 0) {
      return { status: 'partial', label: 'Partial Payment', balance, paid: amountPaid, total };
    } else {
      return { status: 'unpaid', label: 'Payment Due', balance: total, paid: 0, total };
    }
  };

  // ============================================================
  // FULFILLMENT MATH — ONE READER FOR THE WHOLE SCREEN
  //
  // Bug this fixes (Jul 2026): the per-item badge and the Order
  // Status card each did their own status math. The card folded
  // 'out_for_delivery' into the READY bucket, so a dispatched item
  // rendered "Out for Delivery" on its own line while the summary
  // said "ready for delivery". Both now derive from getLineStatus().
  //
  // DATA MODEL: item_statuses is the ONLY fulfillment source. It is
  // UNIQUE on (receipt_id, receipt_item_id) with no count column, so
  // ONE row governs a whole line whatever its quantity — a qty-4 line
  // is 4-of-4 or 0-of-4, never split. No inventory_units path exists
  // here: there is no inventory system, nothing writes units, and
  // reading them only created a second disagreeing opinion.
  //
  // A line therefore has exactly one status. Quantity only ever
  // matters as a MULTIPLIER in the rollup below.
  // ============================================================

  /** Display buckets, least advanced first. */
  const BUCKET_ORDER = ['waiting', 'ready', 'outForDelivery', 'delivered', 'pickedUp'];

  const BUCKET_LABEL = {
    waiting: 'Waiting',
    ready: 'Ready',
    outForDelivery: 'Out for Delivery',
    delivered: 'Delivered',
    pickedUp: 'Picked Up'
  };

  /**
   * The one status for one receipt line.
   * Legacy 'in_warehouse' / 'reserved' / 'ready' rows are read
   * defensively — old data still carries them, and they all mean
   * the same thing to a customer: it's here, waiting for you.
   * @returns {'waiting'|'ready'|'outForDelivery'|'delivered'|'pickedUp'}
   */
  const getLineStatus = (item) => {
    const statusRows = receipt?.itemStatuses || [];
    const receiptItemId = item?.id;
    if (!receiptItemId) return 'waiting';

    const row = statusRows.find(
      s => s.receipt_item_id && String(s.receipt_item_id) === String(receiptItemId)
    );
    if (!row) return 'waiting'; // no row = still on order from the supplier

    // Membership decided once, in statusConstants.js. Add a status
    // there and every reader updates itself.
    if (ITEM_TERMINAL_STATUSES.includes(row.status)) {
      return row.status === 'picked_up' ? 'pickedUp' : 'delivered';
    }
    if (row.status === 'out_for_delivery') return 'outForDelivery';
    if (ITEM_STAGED_STATUSES.includes(row.status)) return 'ready';

    return 'waiting'; // unknown status — never claim more than we know
  };

  const getLineQty = (item) => Math.max(1, parseInt(item?.quantity, 10) || 1);

  // ============================================
  // Order readiness rollup — counts PIECES, not lines, by multiplying
  // each line's single status by its quantity. Consumes getLineStatus,
  // so every number here is the exact total of what the rows above it
  // are showing.
  // ============================================
  const getOrderReadiness = () => {
    const items = receipt?.items || [];
    if (items.length === 0) {
      return {
        ready: 0, total: 0, delivered: 0, outForDelivery: 0, waiting: 0,
        percentage: 0, allReady: false, allDelivered: false, allOutForDelivery: false
      };
    }

    const b = { pickedUp: 0, delivered: 0, outForDelivery: 0, ready: 0, waiting: 0 };
    let totalNeeded = 0;

    items.forEach(item => {
      const qty = getLineQty(item);
      totalNeeded += qty;
      b[getLineStatus(item)] += qty;
    });

    const receivedCount = b.delivered + b.pickedUp; // mixed pickup/delivery both close
    const allDelivered = totalNeeded > 0 && receivedCount >= totalNeeded;
    const remainingItems = totalNeeded - receivedCount;

    // Everything not still on order counts toward progress.
    const percentage = totalNeeded > 0
      ? (allDelivered ? 100 : Math.round(((receivedCount + b.outForDelivery + b.ready) / totalNeeded) * 100))
      : 0;

    // On a truck outranks staged at the store.
    const allOutForDelivery =
      remainingItems > 0 && b.waiting === 0 && b.ready === 0 &&
      b.outForDelivery >= remainingItems;

    // allReady strictly means STAGED AT THE STORE, nothing rolling.
    const allReady =
      remainingItems > 0 && b.waiting === 0 && b.outForDelivery === 0 &&
      b.ready >= remainingItems;

    console.log(
      `📊 Order readiness: ${b.ready} ready, ${b.outForDelivery} out for delivery, ` +
      `${receivedCount} received, ${b.waiting} waiting of ${totalNeeded}`
    );

    return {
      ready: b.ready,
      total: totalNeeded,
      delivered: receivedCount,
      outForDelivery: b.outForDelivery,
      waiting: b.waiting,
      percentage,
      allReady,
      allDelivered,
      allOutForDelivery
    };
  };

  // ============================================
  // THE PIECE RAIL — the receipt's signature element.
  //
  // One segment per physical piece on the order, tinted by state.
  // It replaces four things the old card used to say separately:
  // the status pill, the three count chips, the prose sentence and
  // the progress bar. A customer reads "3 arrived, 1 on the truck,
  // 1 still coming" in a single 6px-tall band.
  //
  // Segments are ordered by progress, not by line order, so it always
  // fills left to right. Past ~14 pieces discrete segments stop being
  // legible, so it collapses to proportional bands.
  // ============================================
  const RAIL_FILL = {
    pickedUp: 'bg-emerald-600',
    delivered: 'bg-emerald-600',
    outForDelivery: 'bg-sky-600',
    ready: 'bg-teal-600',
    waiting: 'bg-slate-300'
  };
  const RAIL_PROGRESSION = ['pickedUp', 'delivered', 'outForDelivery', 'ready', 'waiting'];

  const getPieceRail = () => {
    const counts = { pickedUp: 0, delivered: 0, outForDelivery: 0, ready: 0, waiting: 0 };
    (receipt?.items || []).forEach(item => {
      counts[getLineStatus(item)] += getLineQty(item);
    });
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    const bands = RAIL_PROGRESSION
      .filter(k => counts[k] > 0)
      .map(k => ({ bucket: k, count: counts[k] }));
    return { counts, total, bands, discrete: total > 0 && total <= 14 };
  };

  // Plain-language headline for the current state of the order.
  // Active voice, no filler, one job: tell them what is happening now.
  const getStatusHeadline = (r) => {
    const isPickup = !!(receipt?.delivery_services || receipt?.deliveryServices || {})?.pickup?.enabled;
    if (r.total === 0) return 'No items on this order';
    if (r.allDelivered) return 'Order complete';
    if (r.allOutForDelivery) return 'Out for delivery';
    if (r.outForDelivery > 0) return 'Partly out for delivery';
    if (r.allReady) return isPickup ? 'Ready for pickup' : 'Ready for delivery';
    if (r.delivered > 0) return 'Partly received';
    if (r.ready > 0) return isPickup ? 'Partly ready for pickup' : 'Partly ready';
    return 'On order';
  };

  // One line replacing the old chip cluster. Only non-zero states appear.
  const getStatusDetail = (r) => {
    const parts = [];
    if (r.delivered > 0) parts.push(`${r.delivered} received`);
    if (r.outForDelivery > 0) parts.push(`${r.outForDelivery} out for delivery`);
    if (r.ready > 0) parts.push(`${r.ready} at the store`);
    if (r.waiting > 0) parts.push(`${r.waiting} on order`);
    return parts.join(' · ');
  };

  // Live Delivery Tracker Component - LIGHT MODE
  const LiveDeliveryTracker = () => {
    // Show tracker even for single-stop routes
    if (!deliveryRoute) {
      console.log('⚠️ LiveDeliveryTracker: No deliveryRoute');
      return null;
    }
    
    console.log('🚚 LiveDeliveryTracker rendering:', {
      routeId: deliveryRoute.id,
      routeStatus: deliveryRoute.status,
      myStopIndex,
      currentStopIndex,
      totalStops: routeStops.length,
      driverName: deliveryRoute.driver_name
    });
    
    const stopsAhead = myStopIndex - currentStopIndex;
    const isMyTurn = currentStopIndex === myStopIndex;
    const isDelivered = routeStops[myStopIndex]?.status === 'delivered';
    const isSingleStopRoute = routeStops.length === 1;
    
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
        {/* Header with pulse animation */}
        <div className="relative p-4 sm:p-5 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-cyan-50">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Truck className="w-6 h-6 text-white" />
              </div>
              {!isDelivered && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-4 w-4 bg-blue-500"></span>
                </span>
              )}
            </div>
            <div>
              <h3 className="text-slate-900 font-bold text-lg">
                {isDelivered ? '✓ Delivered!' : isMyTurn ? "You're Next Up!" : 'Out for Delivery'}
              </h3>
              <p className="text-blue-600 text-sm font-medium">
                {isDelivered 
                  ? 'Your order has been delivered'
                  : isMyTurn 
                  ? `${deliveryRoute.driver_name || 'Driver'} is heading your way`
                  : `${deliveryRoute.driver_name || 'Your driver'} is on the way`
                }
              </p>
            </div>
          </div>
        </div>
        
        {/* Animated Route Timeline */}
        <div className="p-4 sm:p-5 bg-slate-50">
          <div className="relative">
            {/* Progress line background */}
            <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200"></div>
            
            {/* Animated progress fill */}
            <div 
              className="absolute left-5 top-0 w-0.5 bg-gradient-to-b from-blue-500 via-cyan-500 to-blue-500 transition-all duration-1000 ease-out"
              style={{ 
                height: `${Math.max(0, ((currentStopIndex + 1) / routeStops.length) * 100)}%`,
              }}
            >
              {/* Animated glow effect */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-blue-500 shadow-lg shadow-blue-500/50 animate-pulse"></div>
            </div>
            
            {/* Store Start Point */}
            <div className="relative flex items-center gap-4 pb-4">
              <div className="relative z-10 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <Home className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-emerald-600 font-semibold text-sm">Store</p>
                <p className="text-slate-400 text-xs">Route started</p>
              </div>
              <CheckCircle className="w-5 h-5 text-emerald-500" />
            </div>
            
            {/* Delivery Stops */}
            <div className="space-y-1">
              {routeStops.map((stop, index) => {
                const isCompleted = stop.status === 'delivered';
                const isCurrent = index === currentStopIndex;
                const isMe = index === myStopIndex;
                const isPast = index < currentStopIndex;
                const isFuture = index > currentStopIndex;
                
                return (
                  <div 
                    key={stop.id}
                    className={`relative flex items-center gap-4 py-3 px-2 rounded-xl transition-all duration-500 ${
                      isMe 
                        ? isCurrent
                          ? 'bg-gradient-to-r from-blue-100 via-cyan-50 to-blue-100 border border-blue-300 scale-[1.02] shadow-md'
                          : 'bg-blue-50 border border-blue-200'
                        : ''
                    }`}
                  >
                    {/* Stop Node */}
                    <div className={`relative z-10 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-500 ${
                      isCompleted
                        ? 'bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg shadow-emerald-500/20'
                        : isCurrent
                        ? 'bg-gradient-to-br from-blue-500 to-cyan-600 shadow-lg shadow-blue-500/30 animate-pulse'
                        : isMe
                        ? 'bg-gradient-to-br from-blue-100 to-cyan-100 border-2 border-blue-400'
                        : 'bg-slate-100 border border-slate-300'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-white" />
                      ) : isCurrent ? (
                        <Truck className="w-5 h-5 text-white" />
                      ) : isMe ? (
                        <MapPin className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Circle className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                    
                    {/* Stop Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-semibold text-sm truncate ${
                          isMe ? 'text-blue-700' : isCompleted ? 'text-emerald-600' : isCurrent ? 'text-slate-900' : 'text-slate-500'
                        }`}>
                          {isMe ? '📍 Your Delivery' : `Stop ${index + 1}`}
                        </p>
                        {isMe && !isCompleted && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full whitespace-nowrap border border-blue-200">
                            {isCurrent ? "You're next!" : `${myStopIndex - currentStopIndex} stops away`}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-400 text-xs truncate">
                        {isMe 
                          ? (receipt?.delivery_address || receipt?.customer_address || 'Your address')
                          : isCompleted 
                          ? 'Delivered ✓' 
                          : isCurrent 
                          ? 'In progress...'
                          : 'Pending'}
                      </p>
                    </div>
                    
                    {/* Status Icon */}
                    <div className="flex-shrink-0">
                      {isCompleted ? (
                        <div className="text-emerald-600 text-xs font-medium">Done</div>
                      ) : isCurrent ? (
                        <div className="flex items-center gap-1">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                          </span>
                          <span className="text-blue-600 text-xs font-medium">Active</span>
                        </div>
                      ) : isMe ? (
                        <Navigation className="w-4 h-4 text-blue-500" />
                      ) : (
                        <Clock className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
      </div>
    );
  };

  // LOADING STATE - LIGHT MODE
  if (loadingReceipt) {
    return (
      <div className="min-h-screen bg-slate-300 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-700 text-lg font-medium">Loading your receipt...</p>
        </div>
      </div>
    );
  }

  // ERROR STATE - LIGHT MODE
  if (error || !receipt) {
    return (
      <div className="min-h-screen bg-slate-300 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-200 shadow-xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Receipt Not Found</h2>
          <p className="text-slate-500 mb-6">
            {error || "We couldn't find this receipt. Please check your link or contact the store."}
          </p>
          <a 
            href="tel:5305550100" 
            className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/20"
          >
            <Phone className="w-5 h-5" />
            <span>Call Store</span>
          </a>
        </div>
      </div>
    );
  }

  // Determine if delivery has begun (drives tracker placement at top of receipt)
  const hasRoute = !!deliveryRoute;
  const hasStops = routeStops.length > 0;
  const routeIsActive = deliveryRoute && ['dispatched', 'in_progress'].includes(deliveryRoute.status);
  const receiptIsOutForDelivery = ['out_for_delivery', 'in_transit', 'partial_out_for_delivery'].includes(receipt.delivery_status);
  const shouldShowTracker = hasRoute && hasStops && (routeIsActive || receiptIsOutForDelivery);
  
  console.log('🎯 Tracker display check:', {
    hasRoute,
    hasStops,
    routeStatus: deliveryRoute?.status,
    routeIsActive,
    receiptDeliveryStatus: receipt.delivery_status,
    receiptIsOutForDelivery,
    shouldShowTracker
  });

  const readiness = getOrderReadiness();
  const rail = getPieceRail();
  const payment = getPaymentStatus();
  const headline = getStatusHeadline(readiness);
  const detail = getStatusDetail(readiness);

  const ITEM_PREVIEW_COUNT = 3;
  const items = receipt.items || [];
  const visibleItems = showAllItems ? items : items.slice(0, ITEM_PREVIEW_COUNT);
  const hiddenCount = items.length - visibleItems.length;

  // Status dot + label per item. No tinted row backgrounds — a filled
  // block behind every row is what made the list read as loud rather
  // than considered. The dot carries the state; the rule carries the
  // structure.
  const DOT = {
    waiting: 'bg-slate-300',
    ready: 'bg-teal-600',
    outForDelivery: 'bg-sky-600',
    delivered: 'bg-emerald-600',
    pickedUp: 'bg-emerald-600'
  };
  const DOT_TEXT = {
    waiting: 'text-slate-500',
    ready: 'text-teal-700',
    outForDelivery: 'text-sky-700',
    delivered: 'text-emerald-700',
    pickedUp: 'text-emerald-700'
  };

  const money = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

  return (
    <div className="min-h-screen py-5 sm:py-10 px-4" style={{ backgroundColor: BRAND.canvas }}>
      <div className="max-w-md mx-auto">

        {/* Live tracker only exists while a truck is actually moving.
            That is the one moment this order deserves its own surface. */}
        {shouldShowTracker && (
          <div className="mb-4">
            <LiveDeliveryTracker />
          </div>
        )}

        {/* ============ THE SHEET ============
            One surface. Sections are separated by hairline rules, not by
            gaps and stacked shadows. This is the whole change: seven
            floating cards read as an app dashboard, one ruled sheet reads
            as a document from a store that knows what it is doing. */}
        <div className="bg-white rounded-2xl shadow-[0_1px_2px_rgba(19,26,36,0.04),0_10px_28px_-14px_rgba(19,26,36,0.16)] ring-1 ring-slate-900/[0.06] overflow-hidden">

          {/* Letterhead rule. The one piece of brand color on the sheet,
              borrowed from printed stationery: a store's identity sits at
              the top edge of the page, it does not tint the page. */}
          <div
            className="h-[3px] w-full"
            style={{ background: `linear-gradient(90deg, ${BRAND.accent} 0%, ${BRAND.accentSoft} 100%)` }}
          />

          {/* --- Masthead --- */}
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-baseline justify-between gap-3">
              <h1 className="text-[17px] font-semibold tracking-[-0.01em] text-slate-900 truncate">
                {receipt.business_name}
              </h1>
              <span className="text-[11px] font-medium text-slate-400 tabular-nums whitespace-nowrap">
                {new Date(receipt.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                <span className="mx-1.5 text-slate-300">/</span>
                {receipt.id?.slice(0, 8).toUpperCase() || '—'}
              </span>
            </div>

            {/* Status is the hero. It is the reason the customer opened
                this link, so it gets the largest type on the sheet. */}
            <div className="mt-5">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-[26px] leading-none font-semibold tracking-[-0.02em] text-slate-900">
                  {headline}
                </h2>
                {parseDateOnly(receipt.delivery_date) && !readiness.allDelivered && (
                  <span className="text-[11px] font-medium text-slate-500 whitespace-nowrap tabular-nums">
                    {parseDateOnly(receipt.delivery_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
              {detail && (
                <p className="mt-1.5 text-[13px] text-slate-500 tabular-nums">{detail}</p>
              )}
            </div>

            {/* --- The piece rail --- */}
            {rail.total > 0 && (
              <div className="mt-4 flex gap-[3px] h-1.5" role="img" aria-label={detail || headline}>
                {rail.discrete
                  ? rail.bands.flatMap(band =>
                      Array.from({ length: band.count }, (_, n) => (
                        <div
                          key={`${band.bucket}-${n}`}
                          className={`flex-1 rounded-full ${RAIL_FILL[band.bucket]}`}
                        />
                      ))
                    )
                  : rail.bands.map(band => (
                      <div
                        key={band.bucket}
                        className={`rounded-full ${RAIL_FILL[band.bucket]}`}
                        style={{ flexGrow: band.count, flexBasis: 0 }}
                      />
                    ))}
              </div>
            )}
          </div>

          {/* --- Items --- */}
          <div className="border-t border-slate-900/[0.07]">
            {visibleItems.map((item, index) => {
              const bucket = getLineStatus(item);
              const qty = getLineQty(item);
              return (
                <div
                  key={item.id || index}
                  className="px-5 py-3 border-b border-slate-900/[0.05] last:border-b-0"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] text-slate-900 truncate">
                        {item.item_name || item.name}
                        {qty > 1 && <span className="text-slate-400 tabular-nums"> ×{qty}</span>}
                      </p>
                      <div className="mt-1 flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${DOT[bucket]}`} />
                        <span className={`text-[11.5px] ${DOT_TEXT[bucket]}`}>
                          {BUCKET_LABEL[bucket]}
                        </span>
                        {item.color && (
                          <span className="text-[11.5px] text-slate-400 truncate">· {item.color}</span>
                        )}
                        {item.sku && (
                          <button
                            type="button"
                            onClick={() => toggleItemDetails(index)}
                            className="text-[11.5px] text-slate-400 hover:text-slate-600 underline decoration-slate-300 underline-offset-2 transition-colors"
                          >
                            {expandedItems[index] ? 'hide' : 'details'}
                          </button>
                        )}
                      </div>
                      {expandedItems[index] && item.sku && (
                        <p className="mt-1.5 text-[11.5px] text-slate-500 tabular-nums">
                          Item #{item.sku}
                          {item.manufacturer?.name && <span className="text-slate-400"> · {item.manufacturer.name}</span>}
                        </p>
                      )}
                    </div>
                    <span className="text-[14px] text-slate-900 tabular-nums flex-shrink-0">
                      {money(item.price)}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Long orders stay on one screen instead of running off it. */}
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAllItems(true)}
                className="w-full px-5 py-2.5 text-[12px] text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors text-left"
              >
                Show {hiddenCount} more {hiddenCount === 1 ? 'item' : 'items'}
              </button>
            )}
            {showAllItems && items.length > ITEM_PREVIEW_COUNT && (
              <button
                type="button"
                onClick={() => setShowAllItems(false)}
                className="w-full px-5 py-2.5 text-[12px] text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors text-left"
              >
                Show less
              </button>
            )}
          </div>

          {/* --- Money --- */}
          <div className="px-5 py-4 border-t border-slate-900/[0.07] bg-slate-50/70">
            <div className="space-y-1.5 text-[13px] tabular-nums">
              <div className="flex justify-between text-slate-500">
                <span>Subtotal</span><span>{money(receipt.subtotal)}</span>
              </div>
              {(() => {
                const ds = receipt.delivery_services || receipt.deliveryServices || {};
                return [
                  { key: 'delivery', label: 'Delivery' },
                  { key: 'setup', label: 'Setup / assembly' },
                  { key: 'wastePickup', label: 'Waste pickup' },
                  { key: 'pickup', label: 'Pickup' },
                ]
                  .filter(s => ds[s.key]?.enabled && (parseFloat(ds[s.key]?.price) || 0) > 0)
                  .map(s => (
                    <div key={s.key} className="flex justify-between text-slate-500">
                      <span>{s.label}</span><span>{money(ds[s.key].price)}</span>
                    </div>
                  ));
              })()}
              <div className="flex justify-between text-slate-500">
                <span>Tax</span><span>{money(receipt.tax)}</span>
              </div>
              <div className="flex justify-between pt-2 mt-1 border-t border-slate-900/[0.07] text-slate-900 font-semibold text-[15px]">
                <span>Total</span><span>{money(receipt.total)}</span>
              </div>
              {payment.paid > 0 && (
                <div className="flex justify-between text-slate-500">
                  <span>Paid</span><span>{money(payment.paid)}</span>
                </div>
              )}
            </div>

            {/* Balance earns emphasis only when there is one to pay. */}
            {payment.status === 'paid' ? (
              <p className="mt-3 text-[12px] text-emerald-700 font-medium">Paid in full</p>
            ) : (
              <div className={`mt-3 flex justify-between items-baseline rounded-lg px-3 py-2.5 tabular-nums ${
                payment.status === 'partial'
                  ? 'bg-amber-50 ring-1 ring-amber-200'
                  : 'bg-rose-50 ring-1 ring-rose-200'
              }`}>
                <span className={`text-[12px] font-medium ${payment.status === 'partial' ? 'text-amber-800' : 'text-rose-800'}`}>
                  Balance due
                </span>
                <span className={`text-[15px] font-semibold ${payment.status === 'partial' ? 'text-amber-800' : 'text-rose-800'}`}>
                  {money(payment.balance)}
                </span>
              </div>
            )}
          </div>

          {/* --- Reply line. The living receipt's whole premise, stated
                once, quietly, instead of in a gradient panel. --- */}
          <div className="px-5 py-3 border-t border-slate-900/[0.07] flex items-center gap-2">
            <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" style={{ color: BRAND.accentSoft }} />
            <p className="text-[12px] text-slate-500">
              Questions about this order? Reply to our text — we answer any time.
            </p>
          </div>
        </div>

        {/* --- Footer: outside the sheet, deliberately quiet --- */}
        <div className="mt-4 px-1 space-y-2 text-center">
          <p className="text-[10.5px] leading-relaxed text-slate-400">
            Reply STOP to opt out, HELP for help. Msg &amp; data rates may apply.
          </p>
          <div className="flex items-center justify-center gap-3 text-[10.5px]">
            <a href="/terms" className="text-slate-400 hover:text-slate-700 transition-colors">Terms</a>
            <span className="text-slate-300">·</span>
            <a href="/privacy-policy" className="text-slate-400 hover:text-slate-700 transition-colors">Privacy</a>
            <span className="text-slate-300">·</span>
            <a
              href="https://exitiumlabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-700 transition-colors"
            >
              <img src="/e.png" alt="" className="w-3 h-3 object-contain" />
              <span className="font-semibold tracking-[0.08em]">EXITIUM</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalReceipt;